import {
  WalletProviderPriority,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';

import type { BitcoinWalletProvider } from '../../BitcoinWalletProvider.types';
import { registerBitcoinNetworkProviderBuilder } from '../../registerBitcoinNetworkProviderBuilder';
import { createBinanceBitcoinWalletProvider } from '../utils/binance/createBinanceBitcoinWalletProvider';
import { getBinanceBitcoinInjectedProvider } from '../utils/binance/getBinanceBitcoinInjectedProvider';
import { createBitgetBitcoinWalletProvider } from '../utils/bitget/createBitgetBitcoinWalletProvider';
import { getBitgetBitcoinInjectedProvider } from '../utils/bitget/getBitgetBitcoinInjectedProvider';
import { createLeatherBitcoinWalletProvider } from '../utils/leather/createLeatherBitcoinWalletProvider';
import { getLeatherBitcoinInjectedProvider } from '../utils/leather/getLeatherBitcoinInjectedProvider';
import { createMagicEdenBitcoinWalletProvider } from '../utils/magicEden/createMagicEdenBitcoinWalletProvider';
import { getMagicEdenInjectedProvider } from '../utils/magicEden/getMagicEdenInjectedProvider';
import { createOkxBitcoinWalletProvider } from '../utils/okx/createOkxBitcoinWalletProvider';
import { getOkxBitcoinInjectedProvider } from '../utils/okx/getOkxBitcoinInjectedProvider';
import { createOneKeyBitcoinWalletProvider } from '../utils/oneKey/createOneKeyBitcoinWalletProvider';
import { getOneKeyBitcoinInjectedProvider } from '../utils/oneKey/getOneKeyBitcoinInjectedProvider';
import { createOylBitcoinWalletProvider } from '../utils/oyl/createOylBitcoinWalletProvider';
import { getOylBitcoinInjectedProvider } from '../utils/oyl/getOylBitcoinInjectedProvider';
import { createPhantomBitcoinWalletProvider } from '../utils/phantom/createPhantomBitcoinWalletProvider';
import { getPhantomInjectedProvider } from '../utils/phantom/getPhantomInjectedProvider';
import { createUnisatWalletProvider } from '../utils/unisat/createUnisatWalletProvider';
import { getUnisatInjectedProvider } from '../utils/unisat/getUnisatInjectedProvider';
import { createXverseBitcoinWalletProvider } from '../utils/xverse/createXverseBitcoinWalletProvider';
import { getXverseInjectedProvider } from '../utils/xverse/getXverseInjectedProvider';

export const BITCOIN_INJECTED_WALLETS_EXTENSION_KEY = 'bitcoinInjectedWallets';

/**
 * Adds the Bitcoin Injected Wallets extension to the Dynamic client.
 *
 * This extension enables integration with many Bitcoin wallets, like MagicEden, Xverse, Unisat, Phantom, and more.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 */
export const addBitcoinInjectedWalletsExtension = (
  client = getDefaultClient()
): void => {
  if (
    hasExtension(
      { extensionKey: BITCOIN_INJECTED_WALLETS_EXTENSION_KEY },
      client
    )
  ) {
    return;
  }

  registerExtension(
    { extensionKey: BITCOIN_INJECTED_WALLETS_EXTENSION_KEY },
    client
  );

  registerBitcoinNetworkProviderBuilder(client);

  const walletProviderRegistry = getWalletProviderRegistry(client);

  const injectedWalletProviders: BitcoinWalletProvider[] = [];

  // Create and register Unisat wallet provider
  const unisatInjectedProvider = getUnisatInjectedProvider();
  if (unisatInjectedProvider) {
    const unisatWalletProvider = createUnisatWalletProvider({
      dynamicClient: client,
      injectedProvider: unisatInjectedProvider,
    });
    injectedWalletProviders.push(unisatWalletProvider);
  }

  // Create and register Phantom wallet provider
  const phantomInjectedProvider = getPhantomInjectedProvider();
  if (phantomInjectedProvider) {
    const phantomWalletProvider = createPhantomBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: phantomInjectedProvider,
    });
    injectedWalletProviders.push(phantomWalletProvider);
  }

  // Create and register Xverse wallet provider
  const xverseInjectedProvider = getXverseInjectedProvider();
  if (xverseInjectedProvider) {
    const xverseWalletProvider = createXverseBitcoinWalletProvider({
      dynamicClient: client,
      satsConnectProvider: xverseInjectedProvider,
    });
    injectedWalletProviders.push(xverseWalletProvider);
  }

  // Create and register MagicEden wallet provider
  const magicEdenInjectedProvider = getMagicEdenInjectedProvider();
  if (magicEdenInjectedProvider) {
    const magicEdenWalletProvider = createMagicEdenBitcoinWalletProvider({
      dynamicClient: client,
      satsConnectProvider: magicEdenInjectedProvider,
    });
    injectedWalletProviders.push(magicEdenWalletProvider);
  }

  // Create and register OKX wallet provider
  const okxInjectedProvider = getOkxBitcoinInjectedProvider();
  if (okxInjectedProvider) {
    const okxWalletProvider = createOkxBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: okxInjectedProvider,
    });
    injectedWalletProviders.push(okxWalletProvider);
  }

  // Create and register OneKey wallet provider
  const oneKeyInjectedProvider = getOneKeyBitcoinInjectedProvider();
  if (oneKeyInjectedProvider) {
    const oneKeyWalletProvider = createOneKeyBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: oneKeyInjectedProvider,
    });
    injectedWalletProviders.push(oneKeyWalletProvider);
  }

  // Create and register Bitget wallet provider
  const bitgetInjectedProvider = getBitgetBitcoinInjectedProvider();
  if (bitgetInjectedProvider) {
    const bitgetWalletProvider = createBitgetBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: bitgetInjectedProvider,
    });
    injectedWalletProviders.push(bitgetWalletProvider);
  }

  // Create and register Binance wallet provider
  const binanceInjectedProvider = getBinanceBitcoinInjectedProvider();
  if (binanceInjectedProvider) {
    const binanceWalletProvider = createBinanceBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: binanceInjectedProvider,
    });
    injectedWalletProviders.push(binanceWalletProvider);
  }

  // Create and register OYL wallet provider
  const oylInjectedProvider = getOylBitcoinInjectedProvider();
  if (oylInjectedProvider) {
    const oylWalletProvider = createOylBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: oylInjectedProvider,
    });
    injectedWalletProviders.push(oylWalletProvider);
  }

  // Create and register Leather wallet provider
  const leatherInjectedProvider = getLeatherBitcoinInjectedProvider();
  if (leatherInjectedProvider) {
    const leatherWalletProvider = createLeatherBitcoinWalletProvider({
      dynamicClient: client,
      injectedProvider: leatherInjectedProvider,
    });
    injectedWalletProviders.push(leatherWalletProvider);
  }

  injectedWalletProviders.forEach((walletProvider) => {
    walletProviderRegistry.register({
      priority: WalletProviderPriority.WINDOW_INJECT,
      walletProvider,
    });
  });
};
